import { Listbox, Transition } from '@headlessui/react'
import type { CSSProperties, FocusEventHandler, ReactNode, RefObject } from 'react'
import { getSelectListboxStyle } from './listboxStyle'
import { SelectDropdownPortal } from './SelectDropdownPortal'
import type { SelectDropdownPositionConfig } from './useSelectDropdownPosition'
import { useSelectDropdownPosition } from './useSelectDropdownPosition'

type SelectListboxRenderArgs = {
  buttonRef: RefObject<HTMLButtonElement | null>
  optionsRef: RefObject<HTMLDivElement | null>
  placement?: 'top' | 'bottom'
  style: CSSProperties
}

export type SelectListboxShellProps = {
  value: string
  onChange: (value: string) => void
  disabled: boolean
  dropdownPositionConfig: SelectDropdownPositionConfig
  rootRef?: RefObject<HTMLDivElement | null>
  onRootBlur?: FocusEventHandler<HTMLDivElement>
  afterEnter?: (updateDropdownPosition: () => void) => void
  afterLeave?: (finishDropdownLeave: () => void) => void
  renderButton: (args: SelectListboxRenderArgs) => ReactNode
  renderOptions: (args: SelectListboxRenderArgs) => ReactNode
}

/**
 * Shared listbox frame for select variants that need identical portal,
 * transition and dropdown-position lifecycle behavior.
 */
export const SelectListboxShell = ({
  value,
  onChange,
  disabled,
  dropdownPositionConfig,
  rootRef,
  onRootBlur,
  afterEnter,
  afterLeave,
  renderButton,
  renderOptions,
}: SelectListboxShellProps) => {
  const {
    buttonRef,
    optionsRef,
    dropdownPosition,
    beginDropdownTransition,
    finishDropdownLeave,
    updateDropdownPosition,
  } = useSelectDropdownPosition(dropdownPositionConfig)

  const renderArgs = {
    buttonRef,
    optionsRef,
    placement: dropdownPosition?.placement,
    style: getSelectListboxStyle(dropdownPosition),
  }

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div ref={rootRef} className="relative" onBlur={onRootBlur}>
        {renderButton(renderArgs)}
        <Transition
          as="div"
          className="contents"
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
          beforeEnter={beginDropdownTransition}
          afterEnter={() => {
            if (afterEnter) {
              afterEnter(updateDropdownPosition)
            } else {
              updateDropdownPosition()
            }
          }}
          afterLeave={() => {
            if (afterLeave) {
              afterLeave(finishDropdownLeave)
            } else {
              finishDropdownLeave()
            }
          }}
        >
          <SelectDropdownPortal>{renderOptions(renderArgs)}</SelectDropdownPortal>
        </Transition>
      </div>
    </Listbox>
  )
}
